import React, { useEffect, useState } from "react";
import { Typography, Box, CircularProgress, Button, TextField } from "@mui/material";
import Pagination from "@mui/material/Pagination";
import { collection, getDocs, doc, setDoc, deleteDoc } from "firebase/firestore";
import { db } from "../../firebase/firebaseConfig";
import SociosTable from "../../components/members/tables/SociosTable";
import EditSocioModal from "../../components/members/modals/EditSocioModal";
import DataVisualizationModal from "../../components/common/DataVisualizationModal";
import CommonDetailsCard from "../../components/common/CommonDetailsCard";
import PagosModal from "../../components/members/modals/PagosModal";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import DeleteMemberModal from "../../components/members/modals/DeleteMemberModal";

const MemberListPage = () => {
  const [socios, setSocios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSocio, setSelectedSocio] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [visualizationOpen, setVisualizationOpen] = useState(false);
  const [pagosOpen, setPagosOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;
  const [deleteOpen, setDeleteOpen] = useState(false);

  useEffect(() => {
    const fetchSocios = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "miembros"));
        const docs = querySnapshot.docs.map(doc => ({
          firebaseId: doc.id,
          ...doc.data()
        }));
        setSocios(docs);
      } catch (error) {
        console.error("Error al obtener los socios:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSocios();
  }, []);

  const handleEdit = (socio) => {
    setSelectedSocio(socio);
    setEditOpen(true);
  };

  const handleVerRegistros = (socio) => {
    setSelectedSocio(socio);
    setVisualizationOpen(true);
  };

  const handleVerPagos = (socio) => {
    setSelectedSocio(socio);
    setPagosOpen(true);
  };

  const handleDelete = (socio) => {
    setSelectedSocio(socio);
    setDeleteOpen(true);
  };

  const confirmDelete = async (socio) => {
    if (!socio?.firebaseId) {
      console.error("ID del socio no definido. No se puede borrar.");
      return;
    }
    try {
      const docRef = doc(db, "miembros", socio.firebaseId);
      await deleteDoc(docRef);
      setSocios(prev => prev.filter(s => s.firebaseId !== socio.firebaseId));
      setSelectedSocio(null);
    } catch (error) {
      console.error("Error al borrar el socio:", error);
    } finally {
      setDeleteOpen(false);
    }
  };

  const handleSaveSocio = async (updatedSocio) => {
    try {
      await setDoc(doc(db, "miembros", updatedSocio.ID_SOCIO.toString()), updatedSocio);
      const updatedList = socios.map(s =>
        s.ID_SOCIO === updatedSocio.ID_SOCIO ? updatedSocio : s
      );
      setSocios(updatedList);
    } catch (error) {
      console.error("Error al guardar el socio:", error);
    }
  };

  const handleExport = () => {
    const worksheet = XLSX.utils.json_to_sheet(socios);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Socios");
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, "socios_gciamt.xlsx");
  };

  const sociosFiltrados = socios
    .filter((s) =>
      (typeof s.NOMBRE_APELLIDO === "string" && s.NOMBRE_APELLIDO.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (typeof s.PAIS === "string" && s.PAIS.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (typeof s.GENERO === "string" && s.GENERO.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (typeof s.ESTADO_MEMBRESIA === "string" && s.ESTADO_MEMBRESIA.toLowerCase().includes(searchTerm.toLowerCase())) ||
      s.ID_SOCIO?.toString().includes(searchTerm)
    )
    .sort((a, b) => a.ID_SOCIO - b.ID_SOCIO);

  const indexOfLast = currentPage * rowsPerPage;
  const indexOfFirst = indexOfLast - rowsPerPage;
  const sociosPaginated = sociosFiltrados.slice(indexOfFirst, indexOfLast);

  const handleChangePage = (event, value) => {
    setCurrentPage(value);
  };

  return (
    <Box display="flex" justifyContent="center" mt={4}>
      <Box sx={{ width: "100%", maxWidth: 1000 }}>
      <Typography variant="h4" gutterBottom>
        Panel de Socios
      </Typography>
      {loading ? (
        <CircularProgress />
      ) : (
        <>
          <TextField
            label="Buscar socio"
            variant="outlined"
            fullWidth
            sx={{ mb: 2 }}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <SociosTable
            socios={sociosPaginated}
            onEdit={handleEdit}
            onVerRegistros={handleVerRegistros}
            onVerPagos={handleVerPagos}
            onDelete={handleDelete}
          />
          <Pagination
            count={Math.ceil(sociosFiltrados.length / rowsPerPage)}
            page={currentPage}
            onChange={handleChangePage}
            color="primary"
            sx={{ mt: 2, display: "flex", justifyContent: "center" }}
          />
        </>
      )}
      <EditSocioModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        socio={selectedSocio}
        onSave={handleSaveSocio}
      />
      <DataVisualizationModal
        open={visualizationOpen}
        onClose={() => setVisualizationOpen(false)}
        title="Detalles del Socio"
      >
        <CommonDetailsCard
          title="Información del Socio"
          data={selectedSocio}
          excludedFields={["ID", "FIREBASEID"]}
          fieldOrder={[
            "NOMBRE_APELLIDO",
            "GENERO",
            "LUGAR_FECHA_NACIMIENTO",
            "IDENTIFICACION",
            "EMAIL",
            "TELEFONOS",
            "PAIS",
            "TIPO_SOCIO",
            "FECHA_MEMBRESIA",
            "FORMACION_PROFESIONAL",
            "CARGO",
            "INSTITUCION_LABORAL",
            "DIRECCION_LABORAL",
            "CARGOS_DESEMPENADOS_GCIAMT",
            "MIEMBRO_OTRAS_SOCIEDADES",
            "ID_SOCIO"
          ]}
        />
      </DataVisualizationModal>
      <PagosModal
        open={pagosOpen}
        onClose={() => setPagosOpen(false)}
        socio={selectedSocio}
      />
      <DeleteMemberModal
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        member={selectedSocio}
        onDelete={confirmDelete}
      />
      </Box>
    </Box>
  );
};

export default MemberListPage;